"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";

const BRAND_TEXT = "MONT2 Academy";
const SUBTITLE = "Data Science • AI • Analytics • Innovation";

/* ── Accent palette ── */
const ACCENT = {
  orange: "#f97316",
  purple: "#8b5cf6",
  blue: "#2016f9",
  cyan: "#00d4c8",
  gold: "#ffd700",
  teal: "#004d5e",
};

const DUST_COLORS: [number, number, number][] = [
  [0, 212, 200],   // cyan
  [249, 115, 22],  // orange
  [255, 215, 0],   // gold
  [139, 92, 246],  // purple
  [0, 77, 94],     // teal
];

const MATH_SYMBOLS = ["Σ", "∫", "π", "λ", "Δ", "μ", "∂", "∇", "≈", "∞"];

export default function Preloader({ onComplete }: { onComplete: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRowRef = useRef<HTMLDivElement>(null);
  const flourishRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const barFillRef = useRef<HTMLDivElement>(null);
  const percentRef = useRef<HTMLSpanElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const [skip, setSkip] = useState(false);

  const stableOnComplete = useCallback(() => {
    if (onComplete) onComplete();
  }, [onComplete]);

  /* ── Session check ── */
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Respect reduced motion
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setSkip(true);
      try { sessionStorage.setItem("m2_visited", "1"); } catch { }
      document.documentElement.classList.remove("needs-preloader");
      stableOnComplete();
      return;
    }

    if (sessionStorage.getItem("m2_visited")) {
      setSkip(true);
      document.documentElement.classList.remove("needs-preloader");
      stableOnComplete();
      return;
    }
  }, [stableOnComplete]);

  /* ── Main animation ── */
  useEffect(() => {
    if (skip) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let frameId: number;

    /* ── Canvas resize ── */
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    /* ── Floating dust particles (55) ── */
    const dots = Array.from({ length: 55 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.4 + 0.3,
      vx: (Math.random() - 0.5) * 0.12,
      vy: (Math.random() - 0.5) * 0.12,
      baseAlpha: Math.random() * 0.35 + 0.06,
      phase: Math.random() * Math.PI * 2,
      depth: 0.5 + Math.random() * 0.5,
    }));

    /* ── Shooting stars ── */
    const spawnStar = () => {
      const fromLeft = Math.random() > 0.5;
      return {
        x: fromLeft ? -10 : canvas.width + 10,
        y: Math.random() * canvas.height * 0.5,
        speed: 3 + Math.random() * 4,
        length: 50 + Math.random() * 90,
        alpha: 0.5 + Math.random() * 0.5,
        width: 0.6 + Math.random() * 1.0,
        angle: fromLeft ? Math.PI * 0.15 : Math.PI * 0.85,
        colorIdx: Math.floor(Math.random() * DUST_COLORS.length),
        active: false,
      };
    };

    const stars = Array.from({ length: 5 }, spawnStar);
    stars.forEach((s, i) => {
      setTimeout(() => { s.active = true; Object.assign(s, spawnStar(), { active: true }); }, 400 + i * 600);
    });

    /* ── Math symbol fragments ── */
    const mathFragments = Array.from({ length: 12 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      symbol: MATH_SYMBOLS[Math.floor(Math.random() * MATH_SYMBOLS.length)],
      alpha: 0,
      targetAlpha: Math.random() * 0.08 + 0.02,
      vx: (Math.random() - 0.5) * 0.08,
      vy: (Math.random() - 0.5) * 0.06,
      size: 10 + Math.random() * 14,
      phase: Math.random() * Math.PI * 2,
    }));

    /* ── Graph wave ── */
    let wavePhase = 0;

    /* ── Draw loop ── */
    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      t += 0.016;

      /* Draw subtle coordinate grid */
      ctx.strokeStyle = "rgba(0,212,200,0.015)";
      ctx.lineWidth = 0.5;
      const gridSize = 80;
      for (let gx = 0; gx < canvas.width; gx += gridSize) {
        ctx.beginPath();
        ctx.moveTo(gx, 0);
        ctx.lineTo(gx, canvas.height);
        ctx.stroke();
      }
      for (let gy = 0; gy < canvas.height; gy += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, gy);
        ctx.lineTo(canvas.width, gy);
        ctx.stroke();
      }

      /* Draw floating dust */
      dots.forEach((d) => {
        const alpha = d.baseAlpha + Math.sin(t * 1.5 + d.phase) * 0.08;
        const c = DUST_COLORS[Math.floor(d.phase * 10) % DUST_COLORS.length];
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r * d.depth, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${Math.max(0, alpha)})`;
        ctx.fill();
        d.x += d.vx * d.depth;
        d.y += d.vy * d.depth;
        if (d.x < -5) d.x = canvas.width + 5;
        if (d.x > canvas.width + 5) d.x = -5;
        if (d.y < -5) d.y = canvas.height + 5;
        if (d.y > canvas.height + 5) d.y = -5;
      });

      /* Draw shooting stars */
      stars.forEach((s) => {
        if (!s.active) return;
        const cosA = Math.cos(s.angle);
        const sinA = Math.sin(s.angle);
        const tailX = s.x - cosA * s.length;
        const tailY = s.y - sinA * s.length;
        const c = DUST_COLORS[s.colorIdx];

        const grad = ctx.createLinearGradient(tailX, tailY, s.x, s.y);
        grad.addColorStop(0, `rgba(${c[0]},${c[1]},${c[2]},0)`);
        grad.addColorStop(0.5, `rgba(${c[0]},${c[1]},${c[2]},${s.alpha * 0.4})`);
        grad.addColorStop(1, `rgba(${c[0]},${c[1]},${c[2]},${s.alpha})`);

        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(s.x, s.y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = s.width;
        ctx.lineCap = "round";
        ctx.stroke();

        // Head glow
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.width + 1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${s.alpha})`;
        ctx.fill();

        s.x += cosA * s.speed;
        s.y += sinA * s.speed;

        if (s.x < -s.length * 2 || s.x > canvas.width + s.length * 2 || s.y > canvas.height + s.length) {
          Object.assign(s, spawnStar(), { active: true });
        }
      });

      /* Draw math symbols */
      mathFragments.forEach((f) => {
        f.alpha += (f.targetAlpha - f.alpha) * 0.01;
        const pulseAlpha = f.alpha + Math.sin(t * 0.8 + f.phase) * 0.015;
        ctx.font = `${f.size}px "DM Sans", sans-serif`;
        ctx.fillStyle = `rgba(0,212,200,${Math.max(0, pulseAlpha)})`;
        ctx.fillText(f.symbol, f.x, f.y);
        f.x += f.vx;
        f.y += f.vy;
        if (f.x < -30) f.x = canvas.width + 20;
        if (f.x > canvas.width + 30) f.x = -20;
        if (f.y < -30) f.y = canvas.height + 20;
        if (f.y > canvas.height + 30) f.y = -20;
      });

      /* Draw subtle graph wave */
      wavePhase += 0.012;
      ctx.beginPath();
      ctx.strokeStyle = "rgba(139,92,246,0.04)";
      ctx.lineWidth = 1.5;
      const waveY = canvas.height * 0.65;
      for (let wx = 0; wx < canvas.width; wx += 3) {
        const y = waveY + Math.sin(wx * 0.008 + wavePhase) * 30 + Math.sin(wx * 0.015 + wavePhase * 1.5) * 15;
        if (wx === 0) ctx.moveTo(wx, y);
        else ctx.lineTo(wx, y);
      }
      ctx.stroke();

      frameId = requestAnimationFrame(draw);
    };
    draw();

    /* ── Letter spans ── */
    const letters = textRowRef.current?.querySelectorAll<HTMLElement>(".preloader-letter");
    if (!letters || letters.length === 0) return;

    /* ── Master GSAP timeline ── */
    const master = gsap.timeline({
      onComplete: () => {
        // Exit sequence
        const exit = gsap.timeline();

        exit
          .to(subtitleRef.current, { opacity: 0, y: -8, duration: 0.3, ease: "power2.in" })
          .to(
            [percentRef.current?.parentElement, barFillRef.current?.parentElement?.parentElement],
            { opacity: 0, y: -8, duration: 0.25, ease: "power2.in" },
            "<0.05"
          )
          .to(
            textRowRef.current,
            { scale: 1.05, opacity: 0, duration: 0.5, ease: "power2.in" },
            "<0.1"
          )
          .to(
            containerRef.current,
            {
              autoAlpha: 0,
              scale: 1.04,
              duration: 0.7,
              ease: "power3.in",
              onComplete: () => {
                if (containerRef.current) {
                  containerRef.current.style.display = "none";
                }
                try { sessionStorage.setItem("m2_visited", "1"); } catch { }
                document.documentElement.classList.remove("needs-preloader");
                stableOnComplete();
              },
            },
            "-=0.2"
          );
      },
    });

    // Phase 1: Ambient glow start
    master.fromTo(
      glowRef.current,
      { opacity: 0, scale: 0.6 },
      { opacity: 0.6, scale: 1, duration: 0.7, ease: "power2.out" }
    );

    // Set 3D perspective on the parent container
    if (textRowRef.current) {
      gsap.set(textRowRef.current, { perspective: 1000 });
    }

    // Phase 2: Futuristic 3D letter stagger reveal (Dominating & Flashy)
    master.fromTo(
      letters,
      {
        opacity: 0,
        y: 45,
        rotationX: -85,
        scale: 0.8,
        transformOrigin: "50% 50% -50",
      },
      {
        opacity: 1,
        y: 0,
        rotationX: 0,
        scale: 1,
        duration: 0.65,
        ease: "back.out(1.7)",
        stagger: 0.05,
      },
      0.2
    );

    // Phase 3: Energy flash / Glow surge synced with completion of letters
    master.to(
      glowRef.current,
      {
        opacity: 1,
        scale: 1.4,
        duration: 0.35,
        ease: "power2.out",
      },
      "-=0.4"
    )
    .to(
      glowRef.current,
      {
        opacity: 0.65,
        scale: 1.1,
        duration: 0.7,
        ease: "power2.inOut",
      }
    );

    // Phase 4: Neon Laser Underline (Shoots symmetrically from center)
    master.fromTo(
      flourishRef.current,
      { scaleX: 0 },
      { scaleX: 1, duration: 0.75, ease: "power3.out" },
      "-=0.7"
    );

    // Phase 5: Cinematic Subtitle Fade & Letter-Spacing expansion
    master.fromTo(
      subtitleRef.current,
      { opacity: 0, y: 15, letterSpacing: "0.2em" },
      { opacity: 0.85, y: 0, letterSpacing: "0.45em", duration: 0.85, ease: "power2.out" },
      "-=0.6"
    );

    // Phase 6: Hold 600ms for appreciation
    master.to({}, { duration: 0.6 });

    // Sync percentage counter to master timeline
    const pObj = { v: 0 };
    gsap.to(pObj, {
      v: 100,
      duration: master.duration(),
      ease: "power1.inOut",
      onUpdate: () => {
        const val = Math.floor(pObj.v);
        if (percentRef.current) percentRef.current.textContent = String(val);
        if (barFillRef.current) barFillRef.current.style.width = `${val}%`;
      },
    });

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(frameId);
      master.kill();
    };
  }, [skip, stableOnComplete]);

  if (skip) return null;

  return (
    <div
      id="preloader"
      ref={containerRef}
      className="fixed inset-0 z-[99999] flex flex-col items-center justify-center overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at center, #0a1628 0%, #050d14 50%, #020509 100%)",
      }}
    >
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />

      {/* Ambient glow */}
      <div
        ref={glowRef}
        className="absolute w-[500px] h-[500px] rounded-full pointer-events-none opacity-0"
        style={{
          background: `radial-gradient(circle, rgba(139,92,246,0.14) 0%, rgba(249,115,22,0.08) 30%, rgba(0,212,200,0.04) 55%, transparent 70%)`,
          filter: "blur(50px)",
        }}
      />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center px-6">
        {/* Signature text */}
        <div className="relative" ref={textRowRef} style={{ transformStyle: "preserve-3d" }}>
          <span
            className="inline-block text-transparent select-none pointer-events-none"
            style={{
              fontFamily: "var(--font-orbitron), sans-serif",
              fontSize: "clamp(2.2rem, 7vw, 5rem)",
              fontWeight: 900,
              lineHeight: 1.2,
              letterSpacing: "0.08em",
            }}
          >
            {BRAND_TEXT.toUpperCase().split("").map((char, i) => (
              <span
                key={i}
                className="preloader-letter inline-block opacity-0"
                style={{
                  background:
                    "linear-gradient(135deg, #f97316 0%, #8b5cf6 45%, #2016f9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "none",
                  filter:
                    "drop-shadow(0 0 25px rgba(139,92,246,0.3)) drop-shadow(0 0 50px rgba(249,115,22,0.1))",
                }}
              >
                {char === " " ? "\u00A0" : char}
              </span>
            ))}
          </span>

          {/* Flourish underline */}
          <div
            ref={flourishRef}
            className="absolute left-[5%] right-[5%] bottom-0"
            style={{
              height: "2px",
              background: `linear-gradient(90deg, transparent 0%, ${ACCENT.purple} 20%, ${ACCENT.blue} 50%, ${ACCENT.purple} 80%, transparent 100%)`,
              transformOrigin: "center center",
              transform: "scaleX(0)",
              boxShadow: `0 0 10px ${ACCENT.purple}66`,
            }}
          />
        </div>

        {/* Subtitle */}
        <span
          ref={subtitleRef}
          className="mt-6 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-medium opacity-0"
          style={{
            fontFamily: "var(--font-dm), sans-serif",
            color: "rgba(255, 252, 252, 1)",
          }}
        >
          {SUBTITLE}
        </span>

        {/* Progress */}
        <div className="mt-5 flex flex-col items-center space-y-2">
          <div
            className="text-xs font-bold tracking-wider"
            style={{
              fontFamily: "var(--font-dm), sans-serif",
              color: ACCENT.gold,
            }}
          >
            <span ref={percentRef}>0</span>
            <span>%</span>
          </div>
          <div className="w-32 h-[1px] bg-white/5 relative overflow-hidden rounded-full">
            <div
              ref={barFillRef}
              className="absolute left-0 top-0 bottom-0 rounded-full"
              style={{
                width: "0%",
                background: `linear-gradient(90deg, ${ACCENT.teal}, ${ACCENT.purple}, ${ACCENT.blue})`,
                boxShadow: `0 0 8px ${ACCENT.purple}88`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
